import React from 'react';

import { ART_STYLE } from '../../theme/tokens';
import {
  createArtStyle,
  deleteArtStyle,
  isMissingStyle,
  listArtStyles,
  summaryOfArtStyle,
  type ArtStyle,
  type ArtStyleSummary,
} from './art-style-api';
import { stampOf } from './sample-jobs';

/** Why the list could not be read: no service / route here, or a failed read (or a failed first draft). */
export type ArtStylesFailure = 'unavailable' | 'failed';

export interface UseArtStyles {
  /** The brand's styles in a stable order (oldest first, so a new one lands beside "New style"); undefined
   *  until the first read lands. */
  items: ArtStyleSummary[] | undefined;
  failure: ArtStylesFailure | null;
  /** A create or duplicate is in flight. */
  creating: boolean;
  /** Re-read the list now; resolves once the read has landed (or was superseded). */
  refresh: () => Promise<void>;
  /** Re-read the list shortly, coalescing a burst of changes into one read. */
  refreshSoon: () => void;
  /** A new blank draft, or a duplicate of `fromStyleId`; null when it failed. */
  create: (fromStyleId?: string) => Promise<ArtStyle | null>;
  /** Delete a style; true once it is gone. */
  remove: (styleId: string) => Promise<boolean>;
  /** A saved record landed: update its card at once (no re-read). */
  adopt: (record: ArtStyle) => void;
  retry: () => void;
}

/** Oldest first, so cards keep their place while the owner edits (the service lists newest-updated first). */
const inStripOrder = (items: readonly ArtStyleSummary[]): ArtStyleSummary[] =>
  [...items].sort((a, b) => stampOf(a.created_at) - stampOf(b.created_at) || a.id.localeCompare(b.id));

/** The style a visit opens on: the active one, else the most recently updated. Pure. */
export const defaultStyleId = (items: readonly ArtStyleSummary[]): string | undefined => {
  const active = items.find((item) => item.status === 'active');
  if (active) return active.id;
  let newest: ArtStyleSummary | undefined;
  for (const item of items) {
    if (!newest || stampOf(item.updated_at) > stampOf(newest.updated_at)) newest = item;
  }
  return newest?.id;
};

/**
 * The brand's art styles as the strip shows them, keyed on `tenantId` (a brand switch drops the previous
 * brand's list and in-flight results). A brand with no styles gets one blank draft, created once per visit
 * (and again after its last style is deleted), so the page always has a style to edit, unless `autoCreate` says
 * otherwise: `false` when the page has something else to show (the brand's catalog looks), `undefined` while that is
 * not known yet (the draft waits).
 */
export const useArtStyles = (tenantId: number | undefined, autoCreate: boolean | undefined = true): UseArtStyles => {
  const [items, setItems] = React.useState<ArtStyleSummary[] | undefined>(undefined);
  /** The brand the held list belongs to, so a brand switch never shows (or selects from) the previous one's. */
  const [itemsTenant, setItemsTenant] = React.useState<number | undefined>(undefined);
  const [failure, setFailure] = React.useState<ArtStylesFailure | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);

  const tenantRef = React.useRef(tenantId);
  tenantRef.current = tenantId;
  const itemsRef = React.useRef(items);
  itemsRef.current = items;
  const genRef = React.useRef(0);
  /** The newest read sent; an older one landing late is ignored. */
  const seqRef = React.useRef(0);
  const creatingRef = React.useRef(false);
  /** The automatic first draft was tried (so a failure is not retried in a loop). */
  const autoCreatedRef = React.useRef(false);
  const soonRef = React.useRef<number | undefined>(undefined);

  const writeCreating = React.useCallback((next: boolean): void => {
    creatingRef.current = next;
    setCreating(next);
  }, []);

  const load = React.useCallback((signal?: AbortSignal): Promise<void> => {
    const tenant = tenantRef.current;
    if (!tenant) return Promise.resolve();
    const gen = genRef.current;
    seqRef.current += 1;
    const seq = seqRef.current;
    return listArtStyles(tenant, signal).then((result) => {
      if (gen !== genRef.current || seq !== seqRef.current) return;
      if (!result.ok) {
        // A failed re-read keeps the cards already shown; only a failed first read is a page failure.
        if (itemsRef.current === undefined) setFailure(result.reason === 'unavailable' ? 'unavailable' : 'failed');
        return;
      }
      setFailure(null);
      setItemsTenant(tenant);
      setItems((prev) => {
        const local = new Map((prev ?? []).map((item) => [item.id, item]));
        // A card updated from a record newer than this read keeps the newer fields.
        const merged = result.value.map((item) => {
          const mine = local.get(item.id);
          return mine && stampOf(mine.updated_at) > stampOf(item.updated_at)
            ? { ...item, ...mine, created_at: item.created_at }
            : item;
        });
        return inStripOrder(merged);
      });
    });
  }, []);

  React.useEffect(() => {
    genRef.current += 1;
    setItems(undefined);
    setFailure(null);
    writeCreating(false);
    autoCreatedRef.current = false;
    window.clearTimeout(soonRef.current);
    if (!tenantId) return undefined;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [tenantId, reloadKey, load, writeCreating]);

  React.useEffect(() => () => window.clearTimeout(soonRef.current), []);

  const refresh = React.useCallback((): Promise<void> => {
    window.clearTimeout(soonRef.current);
    soonRef.current = undefined;
    return load();
  }, [load]);

  const refreshSoon = React.useCallback((): void => {
    window.clearTimeout(soonRef.current);
    soonRef.current = window.setTimeout(() => {
      soonRef.current = undefined;
      void load();
    }, ART_STYLE.LIST_REFRESH_MS);
  }, [load]);

  const adopt = React.useCallback((record: ArtStyle): void => {
    const summary = summaryOfArtStyle(record);
    setItems((prev) => {
      const at = prev?.findIndex((item) => item.id === record.id) ?? -1;
      // A style not in the list (deleted meanwhile) is never brought back by a late save.
      if (!prev || at === -1) return prev;
      if (stampOf(summary.updated_at) < stampOf(prev[at].updated_at)) return prev;
      return prev.map((item, index) => {
        if (index === at) return { ...item, ...summary };
        // At most one style is active: putting this one in use paused the other.
        return summary.status === 'active' && item.status === 'active' ? { ...item, status: 'draft' } : item;
      });
    });
  }, []);

  const create = React.useCallback(
    async (fromStyleId?: string): Promise<ArtStyle | null> => {
      const tenant = tenantRef.current;
      if (!tenant || creatingRef.current) return null;
      const gen = genRef.current;
      writeCreating(true);
      const result = await createArtStyle(tenant, fromStyleId);
      if (gen !== genRef.current) return null;
      writeCreating(false);
      if (!result.ok) return null;
      const record = result.value;
      const card: ArtStyleSummary = { ...summaryOfArtStyle(record), created_at: new Date().toISOString() };
      setItems((prev) => [...(prev ?? []).filter((item) => item.id !== record.id), card]);
      void refresh();
      return record;
    },
    [refresh, writeCreating],
  );

  const remove = React.useCallback(
    async (styleId: string): Promise<boolean> => {
      const tenant = tenantRef.current;
      if (!tenant) return false;
      const result = await deleteArtStyle(tenant, styleId);
      // Already gone (another tab) counts as deleted.
      let gone = result.ok || isMissingStyle(result);
      if (!gone) {
        // A delete the service completed could still come back here as a failure (brand 60828: a 204 the page
        // treated as failed, so the owner was told to retry and the retry hit a 404). Before saying it failed,
        // ask the list whether the style still exists.
        const check = await listArtStyles(tenant);
        gone = check.ok && !check.value.some((item) => item.id === styleId);
      }
      if (!gone) return false;
      autoCreatedRef.current = false;
      // The style is gone whatever else changed meanwhile: drop its card (a mutation, not a stale read).
      if (tenantRef.current === tenant) {
        setItems((prev) => prev?.filter((item) => item.id !== styleId));
        void refresh();
      }
      return true;
    },
    [refresh],
  );

  const current = itemsTenant === tenantId ? items : undefined;

  // A brand with no styles gets a blank draft to start from (when the page has nothing else to show).
  React.useEffect(() => {
    if (autoCreate !== true || !current || current.length > 0 || failure || autoCreatedRef.current) return;
    autoCreatedRef.current = true;
    const gen = genRef.current;
    void create().then((record) => {
      if (!record && gen === genRef.current) setFailure('failed');
    });
  }, [autoCreate, current, failure, create]);

  const retry = React.useCallback((): void => setReloadKey((n) => n + 1), []);

  return { items: current, failure, creating, refresh, refreshSoon, create, remove, adopt, retry };
};

export default useArtStyles;
